import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ref, onValue, update, off } from "firebase/database";
import { getRealtimeDatabase } from "src/utils/firebase";
import { Box, Typography, Button, Paper } from "@mui/material";

export default function SelfiePage() {
  const { uid } = useParams(); // ✅ only uid is needed
  const navigate = useNavigate();
  const [photoUrl, setPhotoUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const db = getRealtimeDatabase();

  // 🟢 Watch session UID
  useEffect(() => {
    if (!uid) return () => {};

    const sessionRef = ref(db, "demo_current_selfie");
    const handler = (snapshot) => {
      const current = snapshot.val();
      const currentUid = current?.uid;

      if (!currentUid || currentUid !== uid) {
        console.log("🚫 Session UID mismatch, redirecting:", currentUid, "≠", uid);
        navigate(`/demo_complete/${uid}/selfie`);
      }
    };

    onValue(sessionRef, handler);
    return () => off(sessionRef, "value", handler);
  }, [navigate, uid, db]);

  // 🟢 Watch user record
  useEffect(() => {
    if (!uid) return () => {};

    const userRef = ref(db, `demo_users/selfie/${uid}`);
    const handler = (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        navigate(`/demo_complete/${uid}/selfie`);
        return;
      }
      if (data.photoUrl) {
        setPhotoUrl(data.photoUrl);
        setLoading(false);
      }
    };

    onValue(userRef, handler);
    return () => off(userRef, "value", handler);
  }, [uid, navigate, db]);

  const requestSnap = async () => {
    if (!uid) return;
    setLoading(true);
    setPhotoUrl(null);
    await update(ref(db, `demo_users/selfie/${uid}`), { snap: true, photoUrl: "" });
  };

  const handleRetry = async () => {
    if (!uid) return;
    setLoading(true);
    setPhotoUrl(null);
    await update(ref(db, `demo_users/selfie/${uid}`), { snap: true, photoUrl: "" });
  };

  const handleHappy = () => {
    if (!uid) return;
    navigate(`/demo_complete/${uid}/selfie`);
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="calc(100vh - 80px)"
      sx={{ backgroundColor: "black", color: "white", p: 2 }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 3,
          width: "100%",
          maxWidth: 420,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <img
          src="/iTarra-LOGO-2022-Outline.webp"
          alt="iTarra Logo"
          style={{ maxWidth: "180px", width: "60%", marginBottom: "25px" }}
        />
        <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold", mb: 2 }}>
          Selfie Time 📸
        </Typography>

        {/* Show request button */}
        {!photoUrl && !loading && (
          <Button
            variant="contained"
            onClick={requestSnap}
            fullWidth
            sx={{
              backgroundColor: "green",
              "&:hover": { backgroundColor: "darkgreen" },
              py: 1.5,
              fontSize: "16px",
              borderRadius: "10px",
            }}
          >
            Take Selfie
          </Button>
        )}

        {/* Loading */}
        {loading && (
          <Typography variant="body1" sx={{ mt: 2 }}>
            📡 Waiting for camera booth...
          </Typography>
        )}

        {/* Display captured selfie */}
        {photoUrl && (
          <Box
            mt={2}
            display="flex"
            flexDirection="column"
            alignItems="center"
            width="100%"
          >
            <img
              src={photoUrl}
              alt="Your selfie"
              style={{
                width: "100%",
                maxWidth: "320px",
                borderRadius: "16px",
                marginBottom: "20px",
              }}
            />

            <Box
              display="flex"
              flexDirection="row"
              gap={2}
              flexWrap="wrap"
              justifyContent="center"
              width="100%"
            >
              <Button
                variant="contained"
                onClick={handleHappy}
                sx={{
                  backgroundColor: "green",
                  "&:hover": { backgroundColor: "darkgreen" },
                  py: 1.2,
                  fontSize: "16px",
                  flex: "1 1 120px",
                }}
              >
                Happy
              </Button>
              <Button
                variant="contained"
                onClick={handleRetry}
                sx={{
                  backgroundColor: "orange",
                  "&:hover": { backgroundColor: "darkorange" },
                  py: 1.2,
                  fontSize: "16px",
                  flex: "1 1 120px",
                }}
              >
                Retry
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
